import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getInitialBrowserLocale, getLocaleDetails, LOCALE_DETAILS, LOCALE_STORAGE_KEY, normalizeLocale, translateCatalog } from "./locales";

const DEFAULT_LOCALIZATION = Object.freeze({
  dir: "ltr",
  isRtl: false,
  locale: "en",
  setLocale: () => "en",
  supportedLocales: LOCALE_DETAILS,
  t: (key, params) => translateCatalog("en", key, params),
});
const LocalizationContext = createContext(DEFAULT_LOCALIZATION);

export const LocalizationProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(getInitialBrowserLocale);

  const setLocale = useCallback((nextLocale, options = {}) => {
    const normalized = normalizeLocale(nextLocale);
    setLocaleState(normalized);
    if (options.persist === false) return normalized;
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
    } catch {
      // Keep the in-memory selection when persistence is unavailable.
    }
    return normalized;
  }, []);

  const details = getLocaleDetails(locale);
  const t = useCallback((key, params) => translateCatalog(locale, key, params), [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = details.isRtl ? "rtl" : "ltr";
  }, [details.isRtl, locale]);

  const value = useMemo(() => ({
    dir: details.isRtl ? "rtl" : "ltr",
    isRtl: details.isRtl,
    locale,
    setLocale,
    supportedLocales: LOCALE_DETAILS,
    t,
  }), [details.isRtl, locale, setLocale, t]);

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
};

export const useLocalization = () => {
  return useContext(LocalizationContext);
};
